import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { useLocalization } from '@/shared/i18n'

import { Button } from './Button'
import { Card } from './Card'
import { Icon } from './Icon'
import { Stack } from './Stack'
import { Text } from './Text'
import './sheet.css'

export interface SheetItem {
  value: string
  name: string
  meta?: string
}

function Shell({
  title,
  bare = false,
  onClose,
  children,
}: {
  title: string
  bare?: boolean
  onClose: () => void
  children: ReactNode
}) {
  const { t } = useLocalization()

  useEffect(() => {
    function escape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', escape)

    return () => window.removeEventListener('keydown', escape)
  }, [onClose])

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label={title}>
      <button
        className="sheet__scrim"
        type="button"
        aria-label={t('app.cancel')}
        onClick={onClose}
      />
      <div className="sheet__panel">
        <span className="sheet__grip" aria-hidden="true" />
        {bare ? null : (
          <Stack direction="row" justify="between" gap="sm">
            <Text as="h2" role="heading-3">
              {title}
            </Text>
            <Button size="sm" variant="quiet" onClick={onClose}>
              {t('app.cancel')}
            </Button>
          </Stack>
        )}
        {children}
      </div>
    </div>
  )
}

// SearchSheet is the long list a chip row cannot hold: every country, every
// month since arrival. It filters as you type and marks what is already chosen,
// because the sheet is also how somebody checks what they picked.
export function SearchSheet({
  title,
  placeholder,
  items,
  chosen,
  onPick,
  onClose,
}: {
  title: string
  placeholder: string
  items: SheetItem[]
  chosen: string[]
  onPick: (value: string) => void
  onClose: () => void
}) {
  const { t } = useLocalization()
  const [query, setQuery] = useState('')
  const field = useRef<HTMLInputElement>(null)

  useEffect(() => {
    field.current?.focus()
  }, [])

  const needle = query.trim().toLowerCase()
  const found = needle ? items.filter((item) => item.name.toLowerCase().includes(needle)) : items

  return (
    <Shell title={title} onClose={onClose}>
      <input
        ref={field}
        className="sheet__search"
        type="search"
        placeholder={placeholder}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      {found.length === 0 ? (
        <Stack gap="xs" className="sheet__empty">
          <Text role="body-strong">{t('sheet.nothing', { query: query.trim() })}</Text>
          <Text role="caption" tone="muted">
            {t('sheet.nothing.body')}
          </Text>
        </Stack>
      ) : (
        <ul className="sheet__list">
          {found.map((item) => (
            <li key={item.value}>
              <button className="sheet__option" type="button" onClick={() => onPick(item.value)}>
                <span className="sheet__name">{item.name}</span>
                {item.meta ? <span className="sheet__meta">{item.meta}</span> : null}
                {chosen.includes(item.value) ? <Icon size="sm">✓</Icon> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Shell>
  )
}

// TextSheet is the picker for an answer no catalogue can hold: there is no
// registry of cities the product could offer, so the person types theirs.
export function TextSheet({
  title,
  placeholder,
  value,
  onSave,
  onClose,
}: {
  title: string
  placeholder: string
  value: string
  onSave: (value: string) => void
  onClose: () => void
}) {
  const { t } = useLocalization()
  const [text, setText] = useState(value)
  const field = useRef<HTMLInputElement>(null)

  useEffect(() => {
    field.current?.focus()
  }, [])

  return (
    <Shell title={title} onClose={onClose}>
      <input
        ref={field}
        className="sheet__search"
        type="text"
        placeholder={placeholder}
        value={text}
        onChange={(event) => setText(event.target.value)}
      />
      <Button full disabled={text.trim() === ''} onClick={() => onSave(text.trim())}>
        {t('app.save')}
      </Button>
    </Shell>
  )
}

// NoticeSheet is the answer to «my country is not on the list»: the product says
// plainly how far it reaches instead of leaving somebody to guess why their
// country is missing.
export function NoticeSheet({
  title,
  heading,
  body,
  accept,
  onAccept,
  onClose,
}: {
  title: string
  heading: string
  body: string
  accept: string
  onAccept: () => void
  onClose: () => void
}) {
  const { t } = useLocalization()

  return (
    <Shell title={title} onClose={onClose}>
      <Card>
        <Stack gap="sm">
          <Icon size="lg">🌍</Icon>
          <Text as="h3" role="title">
            {heading}
          </Text>
          <Text tone="muted" measure>
            {body}
          </Text>
          <Stack gap="sm">
            <Button full onClick={onAccept}>
              {accept}
            </Button>
            <Button full variant="quiet" onClick={onClose}>
              {t('sheet.notify')}
            </Button>
          </Stack>
        </Stack>
      </Card>
    </Shell>
  )
}

// HintSheet explains a limit the product has chosen rather than an answer it
// needs: one button, nothing to decide. NoticeSheet offers a way out, this one
// only says why the door is where it is.
export function HintSheet({
  icon,
  heading,
  body,
  onClose,
}: {
  icon: string
  heading: string
  body: string
  onClose: () => void
}) {
  const { t } = useLocalization()

  return (
    <Shell title={heading} bare onClose={onClose}>
      <Card>
        <Stack gap="sm">
          <Icon size="lg">{icon}</Icon>
          <Text as="h3" role="title">
            {heading}
          </Text>
          <Text tone="muted" measure>
            {body}
          </Text>
          <Button full onClick={onClose}>
            {t('sheet.got_it')}
          </Button>
        </Stack>
      </Card>
    </Shell>
  )
}

export function ActionSheet({
  title,
  onClose,
  children,
}: {
  title: string
  onClose: () => void
  children: ReactNode
}) {
  return (
    <Shell title={title} onClose={onClose}>
      <Stack gap="sm">{children}</Stack>
    </Shell>
  )
}
